/**
 * Onboarding · Step 9 — Publish the deck.
 *
 * The previous "double-click to open" sub-phase was merged into Step 8
 * (DeckWorkingStep) which now handles both the deck-landing animation
 * AND the open prompt. By the time this step mounts the deck detail
 * pane is already open, so we go straight to spotlighting the Publish
 * button.
 *
 * Spotlights `FilePublishButton` (via `data-onboarding-target=
 * "artifact-publish-button"`). Advances to Step 10 when either:
 *   - The deck record's `published_url` becomes non-null (user clicked
 *     Publish and the publish flow finished).
 *   - The right-pane location leaves the file-record route (user
 *     closed the viewer).
 */

import { useFileRecord } from "@/api/fileRecords";
import { usePaneLocation } from "@/components/PaneRouter";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import { memo, useEffect, useRef } from "react";
import {
  useCanvasOnboardingState,
  useCanvasOnboardingUpdate,
} from "../../../../context/CanvasOnboardingContext";
import { Flourish, StepHeader } from "../shared/StepHeader";
import type { StepProps } from "../shared/types";

const FILE_RECORD_PREFIX = "/file-record/";

export const OpenDeckPublishStep = memo(function OpenDeckPublishStep({
  data,
  onAdvance,
}: StepProps) {
  const update = useCanvasOnboardingUpdate();
  const { highlightEmphasisTick } = useCanvasOnboardingState();

  // Re-emphasize the publish-button spotlight on demand — used when
  // the user clicks the in-card "Tap Publish in the artifact toolbar"
  // hint instead of the actual button itself.
  const emphasizePublishHighlight = () => {
    update({ highlightEmphasisTick: highlightEmphasisTick + 1 });
  };

  const rightPane = usePaneLocation("rightPane");
  const panePathname = rightPane?.pathname ?? null;

  const onAdvanceRef = useRef(onAdvance);
  onAdvanceRef.current = onAdvance;

  const deckNodeId =
    typeof data.deckNodeId === "string" ? data.deckNodeId : null;

  const expectedPath = deckNodeId
    ? route(ROUTES.FILE_RECORDS.DETAIL, { fileRecordId: deckNodeId })
    : null;

  const isDeckOpen =
    panePathname !== null &&
    (expectedPath
      ? panePathname.startsWith(expectedPath)
      : panePathname.startsWith(FILE_RECORD_PREFIX));

  // Poll the deck record so a successful publish is detected even if
  // the user stays in the pane afterward.
  const { data: deckRecord } = useFileRecord(deckNodeId ?? undefined, {
    refetchInterval: 2000,
  });
  const isPublished = !!(deckRecord as { published_url?: string } | undefined)
    ?.published_url;

  // Advance when the deck is published OR when the pane closes.
  const advancedRef = useRef(false);
  useEffect(() => {
    if (!isPublished && isDeckOpen) return;
    if (advancedRef.current) return;
    advancedRef.current = true;
    const t = window.setTimeout(() => onAdvanceRef.current(), 500);
    return () => window.clearTimeout(t);
  }, [isPublished, isDeckOpen]);

  useEffect(() => {
    update({
      lockedViewport: false,
      highlightTargetId: "artifact-publish-button",
    });
    return () => {
      update({ highlightTargetId: null });
    };
  }, [update]);

  return (
    <div>
      <StepHeader
        heading={
          <>
            Hit <Flourish>publish</Flourish> to share it.
          </>
        }
        lede={
          <>
            Publishing makes any artifact shareable as a clean link — no need to
            export, screenshot, or stitch anything together. It stays in sync as
            you edit.
          </>
        }
      />

      <div
        className="_co_onb_settle_3"
        role="button"
        tabIndex={0}
        onClick={emphasizePublishHighlight}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            emphasizePublishHighlight();
          }
        }}
        style={{
          marginTop: "var(--co-onb-stack-md)",
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          borderRadius: 999,
          background: "var(--co-snap-fill)",
          border: "1px dashed var(--co-track-strong)",
          fontSize: 13,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          color: "var(--co-fg)",
          fontFamily: "inherit",
          cursor: "pointer",
          userSelect: "none",
          transition: "background-color 200ms ease",
        }}
      >
        <span
          className="_co_onb_dot"
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--co-rust)",
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        <span>Tap Publish in the artifact toolbar</span>
      </div>
    </div>
  );
});
